import api, { clearAuthIntervals } from "./api";
import { jwtDecode } from "jwt-decode";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

export const loginUser = async (email, password) => {
  try {
    const response = await api.post("/accounts/login/", {
      username: email,
      password,
    });
    // Store both tokens
    localStorage.setItem("access_token", response.data.access);
    localStorage.setItem("refresh_token", response.data.refresh);

    // Store user identity
    if (response.data.username) {
      localStorage.setItem("username", response.data.username);
    }
    if (response.data.user_role) {
      localStorage.setItem("user_role", response.data.user_role);
    }

    // Store feature-access flags returned by the login endpoint
    // These drive conditional UI rendering (sidebar links, buttons, etc.)
    localStorage.setItem(
      "offshore_access",
      String(response.data.offshore === true),
    );
    localStorage.setItem(
      "report_access",
      String(response.data.report === true),
    );
    localStorage.setItem("gfi_access", String(response.data.gfi === true));
    localStorage.setItem(
      "vessel_report_access",
      String(response.data.vessel_report === true),
    );
    localStorage.setItem(
      "alert_access",
      String(response.data.alert === true),
    );

    return response.data;
  } catch (error) {
    const errorMsg =
      error.response?.data?.detail ||
      "Login failed. Please check your credentials or try again.";
    throw new Error(errorMsg);
  }
};

// services/authService.js
export const logoutUser = async () => {
  try {
    const refreshToken = localStorage.getItem("refresh_token");
    if (refreshToken) {
      await api.post(
        "/accounts/logout/",
        { refresh: refreshToken },
        { skipAuthRefresh: true },
      );
    }
  } catch (error) {
    console.error("Logout error:", error);
  } finally {
    clearAuthIntervals();
    localStorage.clear();
    window.location.href = "/login";
  }
};

export const getCurrentUser = () => {
  const token = localStorage.getItem("access_token");
  if (!token) return null;

  try {
    const decoded = jwtDecode(token);
    return {
      username: localStorage.getItem("username"),
      role: localStorage.getItem("user_role"),
      expiresAt: decoded.exp * 1000,
    };
  } catch (error) {
    return null;
  }
};

export const createFleet = async (fleetData) => {
  try {
    const response = await api.post("/fleet/orchestrate/", fleetData);
    return response.data;
  } catch (error) {
    console.error("Fleet creation failed:", error.response || error.message);
    throw error; // <-- throw the whole error object
  }
};

export const getMultiVesselUsers = async () => {
  try {
    const response = await api.get("/accounts/list/multi-vessel-users/");
    return response.data;
  } catch (error) {
    console.error(
      "Failed to fetch multi-vessel users:",
      error.response || error.message,
    );
    throw error;
  }
};

export const createMultiVesselUser = async (userData) => {
  try {
    const response = await api.post(
      "/accounts/register/multi-vessel-user/",
      userData,
    );
    return response.data;
  } catch (error) {
    console.error(
      "Failed to create multi-vessel user:",
      error.response || error.message,
    );
    throw error;
  }
};

// New API functions for VesselDetails
export const getAllVessels = async () => {
  try {
    const response = await api.get("/vessels/all-vessels/");
    return response.data;
  } catch (error) {
    console.error("Failed to fetch vessels:", error.response || error.message);
    throw error;
  }
};

export const deleteMultiVesselUser = async (userId) => {
  try {
    const response = await api.delete(
      `/accounts/register/delete-multi-vessel-user/${userId}/`,
    );
    return response.data;
  } catch (error) {
    console.error(
      "Failed to delete multi-vessel user:",
      error.response || error.message,
    );
    throw error;
  }
};

export const updateMultiVesselUser = async (userId, userData) => {
  try {
    const response = await api.patch(
      `/accounts/register/multi-vessel-user/${userId}/`,
      userData,
    );
    return response.data;
  } catch (error) {
    console.error(
      "Failed to update multi-vessel user:",
      error.response || error.message,
    );
    throw error;
  }
};

export const getFleets = async () => {
  try {
    const response = await api.get("/accounts/list/fleets/");
    return response.data;
  } catch (error) {
    console.error("Failed to fetch fleets:", error.response || error.message);
    throw error; // <-- throw the whole error object "Failed to fetch fleets";
  }
};

export const updateFleet = async (fleetId, fleetData) => {
  try {
    const response = await api.patch(
      `/accounts/${fleetId}/update-fleet/`,
      fleetData,
    );
    return response.data;
  } catch (error) {
    console.error("Fleet update failed:", error.response || error.message);
    throw error; // <-- throw the whole error object "Failed to update fleet";
  }
};

export const createFleetUser = async (userData) => {
  try {
    const response = await api.post(`/accounts/register/fleet-user/`, userData);
    return response.data;
  } catch (error) {
    console.error("User creation failed:", error.response || error.message);
    throw error; // <-- throw the whole error object "Failed to create user";
  }
};

// Add this to your existing authService.js exports
export const getFleetUsers = async (fleetId) => {
  try {
    const response = await api.get(`/accounts/${fleetId}/list/fleet-users/`);
    return response.data;
  } catch (error) {
    console.error(
      "Failed to fetch fleet users:",
      error.response || error.message,
    );
    throw error; // <-- throw the whole error object "Failed to fetch fleet users";
  }
};

export const updateFleetUser = async (userId, userData) => {
  try {
    const response = await api.patch(
      `/accounts/${userId}/update-fleet-user/`,
      userData,
    );
    return response.data;
  } catch (error) {
    console.error("User update failed:", error.response || error.message);
    throw error; // <-- throw the whole error object "Failed to update user";
  }
};

export const deleteFleet = async (fleetId) => {
  try {
    const response = await api.delete(`/accounts/${fleetId}/delete-fleet/`);
    return response.data;
  } catch (error) {
    console.error("Failed to delete fleet:", error.response || error.message);
    throw error; // <-- throw the whole error object "Failed to delete fleet";
  }
};

export const deleteFleetUser = async (userId) => {
  try {
    const response = await api.delete(`/accounts/${userId}/delete-fleet-user/`);
    return response.data;
  } catch (error) {
    console.error("Failed to delete user:", error.response || error.message);
    throw error; // <-- throw the whole error object "Failed to delete user";
  }
};

// Fetch vessels for a fleet
export const getFleetVessels = async (fleetId) => {
  try {
    const response = await api.get(`/vessels/${fleetId}/list/`);
    return response.data;
  } catch (error) {
    console.error(
      "Failed to fetch fleet vessels:",
      error.response || error.message,
    );
    throw error; // <-- throw the whole error object "Failed to fetch fleet vessels";
  }
};

export const createFleetVessel = async (vesselData) => {
  try {
    const response = await api.post(`/vessels/create/`, vesselData);
    return response.data;
  } catch (error) {
    console.error("Vessel creation failed:", error.response || error.message);
    throw error; // <-- throw the whole error object "Failed to create Vessel";
  }
};

export const deleteFleetVessel = async (vesselId) => {
  try {
    const response = await api.delete(`/vessels/${vesselId}/delete/`);
    return response.data;
  } catch (error) {
    console.error("Failed to delete vessel:", error.response || error.message);
    throw error; // <-- throw the whole error object "Failed to delete vessel";
  }
};

export const updateFleetVessel = async (vesselId, payload) => {
  try {
    const response = await api.patch(`/vessels/${vesselId}/update/`, payload);
    return response.data;
  } catch (error) {
    console.error("Vessel update failed:", error.response);
    throw error;
  }
};

export const getFleetLoginStats = async (
  year = new Date().getFullYear(),
  fleetId = null,
) => {
  try {
    const params = { year };
    if (fleetId) {
      params.fleet_id = fleetId;
    }
    const response = await api.get("/accounts/fleet-login-stats/", { params });
    return response.data;
  } catch (error) {
    console.error(
      "Failed to fetch fleet login stats:",
      error.response || error.message,
    );
    throw error;
  }
};

/* UserDashboard API*/
export const getFleetDetails = async () => {
  try {
    const response = await api.get("/accounts/fleet-details/");
    return response.data;
  } catch (error) {
    console.error(
      "Failed to fetch fleet details:",
      error.response || error.message,
    );
    throw error;
  }
};

export const getVesselLiveData = async () => {
  try {
    const response = await api.get("/vessels/live-data/");
    return response.data;
  } catch (error) {
    console.error(
      "Failed to fetch vessel live data:",
      error.response || error.message,
    );
    throw error;
  }
};

export const getVesselDetails = async (vesselId) => {
  try {
    const response = await api.get(`/vessels/${vesselId}/vessel-details/`);
    return response.data;
  } catch (error) {
    console.error(
      "Failed to fetch vessel details:",
      error.response || error.message,
    );
    throw error;
  }
};

export const getVesselPortStbdDetails = async (vesselId, params = {}) => {
  try {
    const response = await api.get(`/vessels/${vesselId}/vessel-analyticsScale/`, {
      params: {
        ...params,
        start_time: params.start_date
          ? dayjs(params.start_date).utc().format("YYYY-MM-DDTHH:mm:ssZ")
          : undefined,
        end_time: params.end_date
          ? dayjs(params.end_date).utc().format("YYYY-MM-DDTHH:mm:ssZ")
          : undefined,
      },
    });
    return response.data;
  } catch (error) {
    console.error(
      "Failed to fetch vessel analytics details:",
      error.response || error.message,
    );
    throw error;
  }
};

export const getVesselAnalyticsDetails = async (vesselId, params = {}) => {
  try {
    const response = await api.get(`/vessels/${vesselId}/vessel-analytics/`, {
      params: {
        ...params,
        start_time: params.start_date
          ? dayjs(params.start_date).utc().format("YYYY-MM-DDTHH:mm:ssZ")
          : undefined,
        end_time: params.end_date
          ? dayjs(params.end_date).utc().format("YYYY-MM-DDTHH:mm:ssZ")
          : undefined,
      },
    });
    return response.data;
  } catch (error) {
    console.error(
      "Failed to fetch vessel analytics details:",
      error.response || error.message,
    );
    throw error;
  }
};

export const getVesselMeanAvg = async (vesselId, params = {}) => {
  try {
    // Remove the date formatting from here - let the caller handle it
    const response = await api.get(`/vessels/${vesselId}/vessel-mean-avg/`, {
      params,
    });
    return response;
  } catch (error) {
    console.error(
      "Failed to fetch vessel mean average:",
      error.response || error.message,
    );
    throw error;
  }
};

export const getVesselEnergyDetails = async (vesselId, params = {}) => {
  try {
    const response = await api.get(`/vessels/${vesselId}/vessel-energy/`, {
      params: {
        ...params,
        start_time: params.start_date
          ? dayjs(params.start_date).utc().format("YYYY-MM-DDTHH:mm:ssZ")
          : undefined,
        end_time: params.end_date
          ? dayjs(params.end_date).utc().format("YYYY-MM-DDTHH:mm:ssZ")
          : undefined,
      },
    });
    return response.data;
  } catch (error) {
    console.error(
      "Failed to fetch vessel energy details:",
      error.response || error.message,
    );
    throw error;
  }
};
export const getVesselFuelDetails = async (vesselId, params = {}) => {
  try {
    const { start_date, end_date, type, ...otherParams } = params;
 
    const response = await api.get(
      `/vessels/${vesselId}/vessel-fuelConsumption/`,
      {
        params: {
          ...otherParams,
          type: type || "volume", // Default to volume if not specified
 
          start_date: start_date
            ? dayjs(start_date).utc().format("YYYY-MM-DDTHH:mm:ssZ")
            : undefined,
 
          end_date: end_date
            ? dayjs(end_date).utc().format("YYYY-MM-DDTHH:mm:ssZ")
            : undefined,
        },
      },
    );
 
    return response.data;
  } catch (error) {
    console.error(
      "Failed to fetch vessel fuel details:",
      error.response || error.message,
    );
    throw error;
  }
};

export const getVesselConsumption = async (vesselId, startDate, endDate) => {
  try {
    const response = await api.get(
      `/vessels/${vesselId}/total-consumption-time/`,
      {
        params: {
          start_date: dayjs(startDate).utc().format("YYYY-MM-DDTHH:mm:ss[Z]"),
          end_date: dayjs(endDate).utc().format("YYYY-MM-DDTHH:mm:ss[Z]"),
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error(`Failed to fetch consumption for vessel ${vesselId}:`, error);
    return {
      vessel_info: { imo_number: vesselId, vessel_name: "Unknown" },
      total_consumption_and_time: {
        PortStay: "0|0",
        Standby: "0|0",
        Supply: "0|0",
        Cruising: "0|0",
        total: "0|0",
      },
    };
  }
};


export const getEngineUtilizationDetails = async (vesselId, params = {}) => {
  try {
    const response = await api.get(
      `/vessels/${vesselId}/individual-load-avg/`,
      {
        params: {
          ...params,
          start_time: params.start_date
            ? dayjs(params.start_date).utc().format("YYYY-MM-DDTHH:mm:ssZ")
            : undefined,
          end_time: params.end_date
            ? dayjs(params.end_date).utc().format("YYYY-MM-DDTHH:mm:ssZ")
            : undefined,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error(
      "Failed to fetch engine utilization details:",
      error.response || error.message,
    );
    throw error;
  }
};

export const getPowerManagementData = async (vesselId, timeRange = "") => {
  try {
    const params = {
      time_range: timeRange || "24h", // Fallback to '24h' for empty  // Always set it; backend can handle '' as "last 24hrs"
    };
    const response = await api.get(`/vessels/${vesselId}/hourly-load-avg/`, {
      params,
    });
    return response.data;
  } catch (error) {
    console.error(
      "Failed to fetch Power Management data:",
      error.response || error.message,
    );
    throw error;
  }
};

export const getVesselOverviewDetails = async (vesselId, params = {}) => {
  try {
    let queryParams = {};

    if (params.year && params.month) {
      // Primary: Month/Year query
      queryParams.year = params.year;
      queryParams.month = params.month;
    } else if (params.start_date || params.start_time) {
      // Fallback: Date range (for initial load only)
      queryParams.start_time =
        params.start_date || params.start_time
          ? dayjs(params.start_date || params.start_time)
              .utc()
              .format("YYYY-MM-DDTHH:mm:ssZ")
          : undefined;
      queryParams.end_time =
        params.end_date || params.end_time
          ? dayjs(params.end_date || params.end_time)
              .utc()
              .format("YYYY-MM-DDTHH:mm:ssZ")
          : undefined;
    } else {
      // Default: Current month/year 
      const now = dayjs.utc();
      queryParams.year = now.year();
      queryParams.month = now.month() + 1;
    }

    const response = await api.get(`/vessels/${vesselId}/vessel-overview/`, {
      params: queryParams,
    });
    return response.data;
  } catch (error) {
    console.error(
      "Failed to fetch vessel overview details:",
      error.response?.data || error.message,
    );
    throw error;
  }
};

export const getAllVesselsOperationalModeData = async (params = {}) => {
  try {
    let queryParams = {};

    if (params.year && params.month) {
      // Primary: Month/Year query (pass directly)
      queryParams.year = params.year;
      queryParams.month = params.month;
    } else if (params.start_date || params.start_time) {
      // Fallback: Date range (for initial load only)
      queryParams.start_time =
        params.start_date || params.start_time
          ? dayjs(params.start_date || params.start_time)
              .utc()
              .format("YYYY-MM-DDTHH:mm:ssZ")
          : undefined;
      queryParams.end_time =
        params.end_date || params.end_time
          ? dayjs(params.end_date || params.end_time)
              .utc()
              .format("YYYY-MM-DDTHH:mm:ssZ")
          : undefined;
    } else {
      // Default: Current month/year for initial load
      const now = dayjs().utc();
      queryParams.year = now.year();
      queryParams.month = now.month() + 1; // 11 for November
    }

    const response = await api.get("/vessels/operationalMode-data/", {
      params: queryParams,
    });
    return response.data;
  } catch (error) {
    console.error(
      "Failed to fetch all vessels operational mode data:",
      error.response?.data || error.message,
    );
    throw error;
  }
};



export const getConsumptionReportbyMode = async (vesselId, params = {}) => {
  try {
    let queryParams = {};

    // Handle operational mode - convert from display names to API parameter names
    if (params.operational_mode && params.operational_mode !== "All") {
      const modeMapping = {
        "Supply": "Supply",
        "Cruising": "Cruising",
        "Stand By": "Standby",
        Port: "Port Stay",
      };
      queryParams.operational_mode = modeMapping[params.operational_mode] || params.operational_mode;
    }

    
    // Handle consumption type (volume or mass)
    if (params.type) {
      queryParams.type = params.type;
    } else {
      queryParams.type = "volume"; // default
    }

    // Handle year and month if provided
    if (params.year) queryParams.year = params.year;
    if (params.month) queryParams.month = params.month;

    // If no year/month provided, default to current
    if (!params.year || !params.month) {
      const now = dayjs().utc();
      queryParams.year = queryParams.year || now.year();
      queryParams.month = queryParams.month || now.month() + 1;
    }



    const response = await api.get(`/vessels/${vesselId}/consumption-summary/`, { 
      params: queryParams 
    });

    return response.data;

  } catch (error) {
    console.error("Failed to fetch consumption report by mode:", {
      vesselId,
      params,
      error: error.response?.data || error.message,
    });
    throw error;
  }
};

export const getVesselMainEngineDetails = async (vesselId, params = {}) => {
  try {
    const response = await api.get(
      `/vessels/${vesselId}/vessel-mainEnginStatus/`,
      {
        params: {
          ...params,
          start_time: params.start_date
            ? dayjs(params.start_date).utc().format("YYYY-MM-DDTHH:mm:ssZ")
            : undefined,
          end_time: params.end_date
            ? dayjs(params.end_date).utc().format("YYYY-MM-DDTHH:mm:ssZ")
            : undefined,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error(
      "Failed to fetch vessel main engine details:",
      error.response || error.message,
    );
    throw error;
  }
};

// Add this new function to your services file
export const getVesselData = async () => {
  try {
    const response = await api.get("/vessels/vesselData/");
    return response.data;
  } catch (error) {
    console.error(
      "Failed to fetch vessel data:",
      error.response || error.message,
    );
    throw error;
  }
};

export const submitReport = async (vesselId, reportData) => {
  try {
    const response = await api.post(
      `/vessels/${vesselId}/reports/`,
      reportData,
    );
    return response.data;
  } catch (error) {
    console.error("Failed to submit report:", error.response || error.message);
    throw error;
  }
};

// authService.js
export const updateReport = async (vesselId, reportId, reportData) => {
  try {
    const response = await api.patch(
      `/vessels/${vesselId}/reports/${reportId}/`,
      reportData,
    );
    return response.data;
  } catch (error) {
    console.error(
      "Failed to update report:",
      error.response?.data || error.message,
    );
    throw error;
  }
};

// Existing functions remain the same
export const listReport = async (vesselId) => {
  try {
    const response = await api.get(`/vessels/${vesselId}/reports/list/`);
    return response.data;
  } catch (error) {
    console.error("Failed to fetch reports:", error.response || error.message);
    throw error;
  }
};

export const getVesselReports = async (vesselId, reportId) => {
  try {
    const response = await api.get(
      `/vessels/${vesselId}/reports/${reportId}/view/`,
    );
    return response.data;
  } catch (error) {
    console.error(
      "Failed to fetch vessel reports:",
      error.response || error.message,
    );
    throw error;
  }
};

export const getLastReports = async (vesselId) => {
  try {
    const response = await api.get(
      `/vessels/${vesselId}/last-report-data/`,
    );
    return response.data;
  } catch (error) {
    console.error(
      "Failed to fetch vessel reports:",
      error.response || error.message,
    );
    throw error;
  }
};

export const deleteReport = async (vesselId, reportId) => {
  try {
    const response = await api.delete(
      `/vessels/${vesselId}/reports/${reportId}/delete/`,
    );
    return response.data;
  } catch (error) {
    console.error("Failed to delete report:", error.response || error.message);
    throw error;
  }
};

export const getVesselReportsWithDetails = async () => {
  try {
    const response = await api.get("/vessels/reports/with-details/");
    return response.data;
  } catch (error) {
    console.error(
      "Failed to fetch vessel reports with details:",
      error.response || error.message,
    );
    throw error;
  }
};

// Fetch list of all reports summary for a vessel
export const getVesselReportsList = async (vesselId, startDate = null, endDate = null) => {
  try {
    const params = {};
    if (startDate) params.start_date = startDate; // Already formatted as 2025-12-01T08:00:00Z
    if (endDate) params.end_date = endDate;
 
    const response = await api.get(`/vessels/${vesselId}/reports/list-summary/`, { params });
    return response.data;
  } catch (error) {
    console.error("Failed to fetch vessel reports list:", error.response || error.message);
    throw error;
  }
};
 

// Add this to your authService.js exports
export const getLastReportEntry = async (vesselId) => {
  try {
    const response = await api.get(`/vessels/${vesselId}/reports/lastEntry/`);
    return response.data;
  } catch (error) {
    console.error(
      "Failed to fetch last report entry:",
      error.response || error.message,
    );
    throw error;
  }
};

// authService.js
export const deleteActivity = async (vesselId, reportId, activityId) => {
  try {
    const response = await api.delete(
      `/vessels/${vesselId}/reports/${reportId}/activities/${activityId}/delete/`,
    );
    return response.data;
  } catch (error) {
    console.error(
      "Failed to delete activity:",
      error.response || error.message,
    );
    throw error;
  }
};

export const getVesselFuelConsumptionScaleDetails = async (vesselId, params = {}) => {
  try {
    const response = await api.get(`/vessels/${vesselId}/vessel-fuelConsumptionScale/`, {
      params: {
        ...params,
        start_time: params.start_date
          ? dayjs(params.start_date).utc().format("YYYY-MM-DDTHH:mm:ssZ")
          : undefined,
        end_time: params.end_date
          ? dayjs(params.end_date).utc().format("YYYY-MM-DDTHH:mm:ssZ")
          : undefined,
      },
    });
    return response.data;
  } catch (error) {
    console.error(
      "Failed to fetch vessel energy scale details:",
      error.response || error.message,
    );
    throw error;
  }
};



export const getVesselEnergyScaleDetails = async (vesselId, params = {}) => {
  try {
    const response = await api.get(`/vessels/${vesselId}/vessel-energyScale/`, {
      params: {
        ...params,
        start_time: params.start_date
          ? dayjs(params.start_date).utc().format("YYYY-MM-DDTHH:mm:ssZ")
          : undefined,
        end_time: params.end_date
          ? dayjs(params.end_date).utc().format("YYYY-MM-DDTHH:mm:ssZ")
          : undefined,
      },
    });
    return response.data;
  } catch (error) {
    console.error(
      "Failed to fetch vessel energy scale details:",
      error.response || error.message,
    );
    throw error;
  }
};

export const getVesselCompleteData = async (imoNumber,params = {}) => {
  try {
      const response = await api.get(`/vessels/vessel-completedata/${imoNumber}/`, {
        params: {
         ...(params.start_date && {
          fromTime: dayjs(params.start_date).utc().format("YYYY-MM-DDTHH:mm:ss[Z]")
        }),
        ...(params.end_date && {
          toTime: dayjs(params.end_date).utc().format("YYYY-MM-DDTHH:mm:ss[Z]")
         }),
      },
    });
    return response.data;
  } catch (error) {
    console.error(
      "Failed to fetch vessel complete data:",
      error.response || error.message,
    );
    throw error;
  }
};
 


export const getVesselBunkeringData = async (vesselId, params = {}) => {
  try {
    const response = await api.get(`/vessels/${vesselId}/bunkering-data/`, {
      params: {
        // Optional date filtering - pass start_date and end_date as YYYY-MM-DD
        start_date: params.start_date || undefined,
        end_date: params.end_date || undefined,
      },
    });
    return response.data;
  } catch (error) {
    console.error(
      "Failed to fetch vessel bunkering data:",
      error.response?.data || error.message
    );
    throw error;
  }
};

export const getDailyConsumption = async (vesselId, filter = "24h") => {
  try {
    const params = {
      filter: filter || "24h", // Default to 24h if empty
    };
   
    const response = await api.get(`/vessels/${vesselId}/consumption-report/`, {
      params,
    });
   
    return response.data;
  } catch (error) {
    console.error(
      "Failed to fetch daily consumption data:",
      error.response || error.message,
    );
    throw error;
  }
};
// Add these to your existing authService.js file

// Password Reset API functions
export const forgotPassword = async (email) => {
  try {
    const response = await api.post("/accounts/forgot-password/", {
      email,
    });
    return response.data; // { message: "Password reset link has been sent to your email.", status: "success" }
  } catch (error) {
    console.error("Forgot password error:", error.response || error.message);
    const errorMsg = error.response?.data?.message || 
                     error.response?.data?.error || 
                     "Failed to send reset link. Please try again.";
    throw new Error(errorMsg);
  }
};

export const validateResetToken = async (token) => {
  try {
    const response = await api.get(`/accounts/validate-reset-token/?token=${token}`);
    return response.data; // { valid: true, email: "user@example.com", username: "USERNAME" }
  } catch (error) {
    console.error("Token validation error:", error.response || error.message);
    const errorMsg = error.response?.data?.message || 
                     error.response?.data?.error || 
                     "Invalid or expired token.";
    throw new Error(errorMsg);
  }
};

export const resetPassword = async (newPassword, confirmPassword, token) => {
  try {
    const response = await api.post("/accounts/reset-password/", {
      new_password: newPassword,
      confirm_password: confirmPassword,
      token: token,
    });
    return response.data; // { message: "Password has been reset successfully.", status: "success" }
  } catch (error) {
    console.error("Reset password error:", error.response || error.message);
    const errorMsg = error.response?.data?.message || 
                     error.response?.data?.error || 
                     "Failed to reset password. Please try again.";
    throw new Error(errorMsg);
  }
};
// Add to authService.js
export const getUniqueParameterNames = async () => {
  try {
    const response = await api.get("/vessels/vessel-parameters/unique-names/");
    return response.data;
  } catch (error) {
    console.error("Failed to fetch unique parameter names:", error.response || error.message);
    throw error;
  }
};

// Delete a vessel parameter
export const deleteVesselParameter = async (parameterId) => {
  try {
    const response = await api.delete(`/vessels/vessel-parameters/${parameterId}/delete/`);
    return response.data;
  } catch (error) {
    console.error("Failed to delete vessel parameter:", error.response || error.message);
    throw error;
  }
};
export const getVesselAlerts = async (imoNumber = null, params = {}) => {
  try {
    let url = "/vessels/vessel-alerts/";
    const queryParams = {};
    
    // Format dates if provided (matching other API functions)
    if (params.start_date) {
      queryParams.start_date = dayjs(params.start_date).utc().format("YYYY-MM-DDTHH:mm:ssZ");
    }
    if (params.end_date) {
      queryParams.end_date = dayjs(params.end_date).utc().format("YYYY-MM-DDTHH:mm:ssZ");
    }
    
    if (imoNumber) {
      // Use IMO number in the URL
      url = `/vessels/vessel-alerts/${imoNumber}/`;
    }
    
    const response = await api.get(url, { params: queryParams });
    return response.data;
  } catch (error) {
    console.error("Failed to fetch vessel alerts:", error.response || error.message);
    throw error;
  }
};

// Add this to your authService.js

export const getTaskStatus = async (taskId) => {
  try {
    const response = await api.get(`/vessels/task-status/${taskId}/`);
    // Ensure we received JSON, not an HTML error page
    if (typeof response.data !== "object" || response.data === null) {
      throw new Error("Invalid response from task-status endpoint — received non-JSON.");
    }
    return response.data;
  } catch (error) {
    // Surface a clean message instead of the raw axios error
    const detail =
      error.response?.data?.detail ||
      error.response?.data ||
      error.message ||
      "Unknown error polling task status.";
    console.error("Failed to get task status:", detail);
    throw new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
  }
};

// Update your existing triggerMissingDataPull function
export const triggerMissingDataPull = async (params) => {
  try {
    const response = await api.post("/vessels/trigger-missing-data-pull/", params);
    return response.data;
  } catch (error) {
    console.error("Failed to trigger data pull:", error.response || error.message);
    throw error;
  }
};
export const getTaskLogs = async (taskId) => {
  try {
    const response = await api.get(`/vessels/task-logs/?task_id=${taskId}`);
    if (typeof response.data !== "object" || response.data === null) {
      throw new Error("Invalid response from task-logs endpoint.");
    }
    return response.data;
  } catch (error) {
    const detail =
      error.response?.data?.detail || error.message || "Unknown error fetching task logs.";
    console.error("Failed to get task logs:", detail);
    throw new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
  }
};


// ── Equipment Master (vessel-aware filtered list) ────────────
export const getEquipmentMaster = async (vesselId) => {
  try {
    const response = await api.get(`/vessels/${vesselId}/equipment-master/`);
    return response.data;
  } catch (error) {
    console.error("Failed to fetch equipment master:", error.response || error.message);
    throw error;
  }
};

// ── Equipment Parameters — list all for a vessel ─────────────
export const getEquipmentParameters = async (vesselId) => {
  try {
    const response = await api.get(`/vessels/${vesselId}/equipment-parameters/`);
    return response.data;
  } catch (error) {
    console.error("Failed to fetch equipment parameters:", error.response || error.message);
    throw error;
  }
};

// ── Equipment Parameters — create ────────────────────────────
export const createEquipmentParameter = async (vesselId, payload) => {
  try {
    const response = await api.post(`/vessels/${vesselId}/equipment-parameters/`, payload);
    return response.data;
  } catch (error) {
    console.error("Failed to create equipment parameter:", error.response || error.message);
    throw error;
  }
};

// ── Equipment Parameters — update (partial) ──────────────────
export const updateEquipmentParameter = async (vesselId, paramId, payload) => {
  try {
    const response = await api.patch(`/vessels/${vesselId}/equipment-parameters/${paramId}/`, payload);
    return response.data;
  } catch (error) {
    console.error("Failed to update equipment parameter:", error.response || error.message);
    throw error;
  }
};

// ── Equipment Parameters — delete ────────────────────────────
export const deleteEquipmentParameter = async (vesselId, paramId) => {
  try {
    const response = await api.delete(`/vessels/${vesselId}/equipment-parameters/${paramId}/`);
    return response.data;
  } catch (error) {
    console.error("Failed to delete equipment parameter:", error.response || error.message);
    throw error;
  }
};
